import { IResourceInfo, IView, IViewEditable, IViewInfo, IViewResource } from "../../../view/IView";
import {
	createEventEmitter,
	emitEvent,
	offEvent,
	onEvent,
	removeAllEventListeners,
	TGetSimpleEventListener
} from "@adapptio/lib-shared";
import { ABILITY_ACTIONS, AbilityService } from "../../../ability/AbilityService";
import { IEditorResource_Theme } from "../../../resources/ResourceTypes";
import { ResourceService } from "../../../resources/ResourceService";
import { IEditor } from "../../../IEditor";
import { IResourceDescription } from "@adapptio/lib-core";
import { LayoutState, LayoutState_Tabs } from "../../../ui/components/Layout/LayoutState";
import { ThemeEditorLayoutConfig } from "./ThemeEditorLayout";
import { ThemeModel } from "../Common/ThemeModel";
import { RuntimeService } from "../../../runtime/RuntimeService";
import { ViewService } from "../../../view/ViewService";

interface IThemeEditorParams {
	resourceId: string;
}

export class ThemeEditor implements IView<IThemeEditorParams>, IViewEditable, IViewResource {
	public readonly onViewInfoChanged = createEventEmitter<void>();
	public readonly onResourceInfoChanged = createEventEmitter<void>();

	private readonly abilitySvc: AbilityService;
	private readonly resourceService: ResourceService;
	private readonly runtimeService: RuntimeService;
	private readonly viewService: ViewService;

	private readonly themeModel: ThemeModel;

	private readonly layout = new LayoutState(ThemeEditorLayoutConfig);

	private viewId: string;

	private resource: IEditorResource_Theme;
	private resourceDescriptor: IResourceDescription;

	private saveInProgress = false;
	private isModified = false;

	constructor(dc: IEditor) {
		this.abilitySvc = dc.get("abilityService");
		this.resourceService = dc.get("resourceService");
		this.runtimeService = dc.get("runtimeService");
		this.viewService = dc.get("viewService");

		this.themeModel = new ThemeModel(this.runtimeService);
	}

	public async init(params: IThemeEditorParams, viewId: string): Promise<void> {
		this.viewId = viewId;

		this.unsubscribeInitEvents();

		this.resourceDescriptor = this.resourceService.getResourceDescriptorById(params.resourceId);
		this.resource = (await this.resourceService.getBlueprintById(
			params.resourceId
		)) as IEditorResource_Theme;

		emitEvent(this.onViewInfoChanged);

		if (!this.resource) {
			throw new Error(`Resource '${params.resourceId}' was not found.`);
		}

		await this.themeModel.loadDocument(this.resource.content);

		this.subscribeInitEvents();
	}

	public dispose(): void {
		removeAllEventListeners(this.onViewInfoChanged);
		removeAllEventListeners(this.onResourceInfoChanged);

		this.unsubscribeInitEvents();

		this.themeModel?.dispose();
	}

	private subscribeInitEvents(): void {
		onEvent(this.themeModel.onModelChange, this.handleModelChange);
		onEvent(this.resource.onChange, this.handleResourceChange);
		onEvent(this.resource.onDelete, this.handleResourceDelete);

		onEvent(this.themeModel.getSelection().onSelectSingle, this.handleSelectSingle);
		onEvent(this.themeModel.getSelection().onSelectSelected, this.handleSelectSingle);
	}

	private unsubscribeInitEvents(): void {
		if (this.themeModel) {
			offEvent(this.themeModel.onModelChange, this.handleModelChange);

			offEvent(this.themeModel.getSelection().onSelectSingle, this.handleSelectSingle);
			offEvent(this.themeModel.getSelection().onSelectSelected, this.handleSelectSingle);
		}

		if (this.resource) {
			offEvent(this.resource.onChange, this.handleResourceChange);
			offEvent(this.resource.onDelete, this.handleResourceDelete);
		}
	}

	private handleSelectSingle = (): void => {
		this.layout.getById<LayoutState_Tabs>("panelRight").selectTab("inspector");
	};

	private handleModelChange: TGetSimpleEventListener<ThemeModel["onModelChange"]> = () => {
		this.setIsModified(true, true);
	};

	private handleResourceChange: TGetSimpleEventListener<IEditorResource_Theme["onChange"]> = () => {
		this.resourceDescriptor = this.resource.descriptor;
		emitEvent(this.onViewInfoChanged);
		emitEvent(this.onResourceInfoChanged);
	};

	private handleResourceDelete: TGetSimpleEventListener<IEditorResource_Theme["onDelete"]> = ({
		deletedByLocalAction
	}) => {
		if (deletedByLocalAction || !this.isModified) {
			this.isModified = false;
			this.viewService.close(this.viewId);
		}
	};

	public async save(): Promise<boolean> {
		if (!this.resource) {
			throw new Error("Resource was not loaded.");
		}

		if (!this.isModified || this.saveInProgress) {
			return false;
		}

		const content = this.themeModel.exportDocumentAsYAML();

		this.setSaveInProgress(true);

		this.setIsModified(false, false);

		try {
			await this.resourceService.saveBlueprint(
				this.resource.id,
				content,
				this.resource.metaData.revision,
				"Editor Save"
			);

			this.setIsModified(this.isModified, true);
		} catch (err) {
			this.setIsModified(true, true);
			throw err;
		} finally {
			this.setSaveInProgress(false);
		}

		return true;
	}

	public isBeingSaved(): boolean {
		return this.saveInProgress;
	}

	private setIsModified(isModified: boolean, notify: boolean) {
		this.isModified = isModified;

		if (notify) {
			emitEvent(this.onViewInfoChanged);
		}
	}

	public undo() {
		this.themeModel.undoRedoManager.undo();
		this.setIsModified(true, true);
	}

	public redo() {
		this.themeModel.undoRedoManager.redo();
		this.setIsModified(true, true);
	}

	public canUndo() {
		return this.themeModel.undoRedoManager.canUndo();
	}

	public canRedo() {
		return this.themeModel.undoRedoManager.canRedo();
	}

	private setSaveInProgress(saveInProgress: boolean) {
		this.saveInProgress = saveInProgress;
		emitEvent(this.onResourceInfoChanged);
	}

	public canSave(): boolean {
		return (
			this.isModified &&
			!this.isBeingSaved() &&
			this.abilitySvc.can(ABILITY_ACTIONS.WRITE, this.resource)
		);
	}

	public getThemeModel(): ThemeModel {
		return this.themeModel;
	}

	public getLayout(): LayoutState {
		return this.layout;
	}

	public getResourceInfo(): IResourceInfo {
		return {
			resource: this.resource,
			parentList: this.resourceService.getResourceParentList(this.resource.id),
			isBeingSaved: this.isBeingSaved()
		};
	}

	public getViewInfo(): IViewInfo {
		return {
			label: this.resourceDescriptor?.name || "...",
			icon: "mdi/shape-outline",
			canClose: true,
			canSave: this.canSave(),
			isModified: this.isModified,
			renderWhenNotActive: true,
			tabStyleModifier: {
				"resource-type-theme": true
			}
		};
	}
}
